import {
  LayoutListIcon,
  PalmtreeIcon,
  FileCheckIcon,
  UserCogIcon,
  SailboatIcon,
  CircleDollarSignIcon,
  DollarSign,
  BathIcon,
  BookCheckIcon,
  UsersIcon,
  Receipt,
  HomeIcon,
  KanbanSquareIcon,
  SearchIcon,
  InboxIcon, ClockIcon } from "lucide-react";
import { User, WorkspaceRole, workspaceRole } from "./stores/userStore";

export type MenuItem = {
  label: string;
  icon: JSX.Element;
  href: string;
  superAdminOnly: boolean;
  /** Roles that see this item. `admin` sees everything regardless. */
  roles: WorkspaceRole[];
};

export type MenuSection = {
  label: string;
  items: MenuItem[];
};

/**
 * Workspace IA: grouped, role-gated sections per the integration handoff
 * (docs/design-handoff/handoff-integration, ws-ia.js). Admin sees every item;
 * other roles see their slice. Nav visibility is UX only — real enforcement is
 * the backend requireRole gates. Money-side screens beyond the existing payment
 * tables (payments inbox, receivables, payables, commission) arrive in Phase D
 * and slot into the Money group.
 */
export const MENU_SECTIONS: MenuSection[] = [
  {
    label: "Work",
    items: [
      { label: "Home", icon: <HomeIcon />, href: "/", superAdminOnly: false, roles: ["broker", "ops", "finance", "viewer"] },
      { label: "My queue", icon: <KanbanSquareIcon />, href: "/queue", superAdminOnly: false, roles: ["broker"] },
      { label: "Charter queue", icon: <ClockIcon />, href: "/charter-queue", superAdminOnly: false, roles: ["ops", "finance"] },
      { label: "Enquiries", icon: <InboxIcon />, href: "/enquiries", superAdminOnly: false, roles: ["broker", "ops"] },
      { label: "Offers", icon: <LayoutListIcon />, href: "/offers", superAdminOnly: false, roles: ["broker", "ops"] },
      { label: "Options", icon: <BookCheckIcon />, href: "/options", superAdminOnly: false, roles: ["broker", "ops"] },
      { label: "Bookings", icon: <FileCheckIcon />, href: "/bookings", superAdminOnly: false, roles: ["broker", "ops", "finance", "viewer"] },
      { label: "Fleet search", icon: <SearchIcon />, href: "/fleet", superAdminOnly: false, roles: ["broker", "ops"] },
    ],
  },
  {
    label: "Fleet & data",
    items: [
      { label: "Yachts", icon: <SailboatIcon />, href: "/yachts", superAdminOnly: false, roles: ["broker", "ops"] },
      {
        label: "Operators",
        icon: <UserCogIcon />,
        href: "/operators",
        superAdminOnly: false,
        // Every role: the whole team reaches operator records for boarding info.
        roles: ["broker", "ops", "finance", "viewer"],
      },
      { label: "Amenities", icon: <BathIcon />, href: "/amenities", superAdminOnly: false, roles: ["ops"] },
      { label: "Locations", icon: <PalmtreeIcon />, href: "/locations", superAdminOnly: false, roles: ["ops"] },
      { label: "Currencies", icon: <CircleDollarSignIcon />, href: "/currencies", superAdminOnly: false, roles: ["ops", "finance"] },
      { label: "Customers", icon: <UsersIcon />, href: "/customers", superAdminOnly: false, roles: ["broker", "ops"] },
    ],
  },
  {
    label: "Money",
    items: [
      { label: "Payments inbox", icon: <InboxIcon />, href: "/payments-inbox", superAdminOnly: false, roles: ["ops", "finance", "broker"] },
      { label: "Payments", icon: <DollarSign />, href: "/payments", superAdminOnly: false, roles: ["ops", "finance"] },
      { label: "Receivables", icon: <Receipt />, href: "/receivables", superAdminOnly: false, roles: ["ops", "finance"] },
      { label: "Payables", icon: <Receipt />, href: "/payables", superAdminOnly: false, roles: ["ops", "finance"] },
      { label: "CA bookings", icon: <SailboatIcon />, href: "/ca", superAdminOnly: false, roles: ["ops", "finance", "broker"] },
      { label: "Payments Summary", icon: <Receipt />, href: "/payments-summary", superAdminOnly: true, roles: ["finance"] },
    ],
  },
];

export function menuItemVisible(item: MenuItem, user: User) {
  const role = workspaceRole(user);
  if (item.superAdminOnly && !user.superAdmin) return false;
  return role === "admin" || item.roles.includes(role);
}

/** Sections filtered to what this user sees (empty sections dropped). */
export function visibleMenuSections(user: User): MenuSection[] {
  return MENU_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => menuItemVisible(item, user)),
  })).filter((section) => section.items.length > 0);
}

/** Default landing route per role. Brokers land on their queue; every other
 *  role's daily surface is its role-aware Home at "/" (ops departures/funding,
 *  finance money in/out, admin/viewer commercial summary). */
export function defaultLanding(user: User): string {
  return workspaceRole(user) === "broker" ? "/queue" : "/";
}

/** Flat list for consumers that don't render groups (mobile menu, height calc). */
export const MENU: MenuItem[] = MENU_SECTIONS.flatMap((s) => s.items);
